import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Dropdown,
  Form,
  Nav,
  ProgressBar,
  Row,
  Tab,
  Table,
} from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertTriangle, Check, X, AlertCircle } from 'react-feather';
import { toast } from 'react-toastify';
import {
  getTenantDetailAPI,
  suspendTenantAPI,
  updateTenantAPI,
} from '../../../Services/mockApi';
import PageHeader from '../../../Components/shared/PageHeader';
import LoadingSkeleton from '../../../Components/shared/LoadingSkeleton';
import EmptyState from '../../../Components/shared/EmptyState';
import StatusBadge from '../../../Components/shared/StatusBadge';
import ConfirmModal from '../../../Components/shared/ConfirmModal';
import { platformUrl } from '../../../utils/mhNav';
import styles from './TenantDetail.module.scss';

const SUPPORT_KEY = 'mh_support_session';

function readSupport(tenantId) {
  try {
    const raw = JSON.parse(sessionStorage.getItem(SUPPORT_KEY) || 'null');
    if (!raw || raw.tenantId !== tenantId) return null;
    if (Date.now() > raw.until) return null;
    return raw;
  } catch {
    return null;
  }
}

function usageColor(pct) {
  if (pct > 90) return 'danger';
  if (pct >= 70) return 'warning';
  return 'success';
}

export default function TenantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notes, setNotes] = useState('');
  const [supportModal, setSupportModal] = useState(false);
  const [suspendModal, setSuspendModal] = useState(false);
  const [supportSession, setSupportSession] = useState(null);
  const [tab, setTab] = useState('overview');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getTenantDetailAPI(id);
      setData(res.data);
      setNotes(res.data.notes || '');
      setSupportSession(readSupport(id));
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setSupportSession(readSupport(id));
  }, [id]);

  const saveNote = async () => {
    try {
      await updateTenantAPI(id, { notes });
      toast.success('Note saved');
      load();
    } catch (e) {
      toast.error(e.message || 'Save failed');
    }
  };

  const onboardingIcon = (status) => {
    if (status === 'done') return <Check className="text-success" size={18} />;
    if (status === 'warning') return <AlertCircle className="text-warning" size={18} />;
    return <X className="text-danger" size={18} />;
  };

  const healthCards = useMemo(() => {
    if (!data) return [];
    return [
      { title: 'Active Users', value: data.totalUsers },
      { title: 'Meetings This Month', value: data.usage?.meetings?.used ?? '—' },
      { title: 'Failed Jobs', value: data.failedJobsCount ?? 2 },
      { title: 'Billing Status', value: data.billingStatus === 'past_due' ? 'Past due' : 'Current' },
    ];
  }, [data]);

  if (loading && !data) {
    return (
      <div>
        <PageHeader
          title="Tenant"
          breadcrumbs={[
            { label: 'Platform', path: platformUrl('dashboard') },
            { label: 'Tenants', path: platformUrl('tenants') },
            { label: id },
          ]}
        />
        <LoadingSkeleton rows={6} cols={3} />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div>
        <PageHeader
          title="Tenant"
          breadcrumbs={[
            { label: 'Platform', path: platformUrl('dashboard') },
            { label: 'Tenants', path: platformUrl('tenants') },
            { label: id },
          ]}
        />
        <EmptyState
          icon={AlertTriangle}
          title="Could not load tenant"
          description={error?.message || 'Not found'}
          actionLabel="Retry"
          onAction={load}
        />
      </div>
    );
  }

  const sub = data.subscription;
  const integ = data.integrations;
  const usage = data.usage;

  return (
    <div>
      {data.billingStatus === 'past_due' && (
        <Alert variant="danger" className="mb-3">
          Billing is past due — some features may be restricted until payment is resolved.
        </Alert>
      )}
      {supportSession && (
        <Alert variant="warning" className={`mb-3 ${styles.supportRibbon}`}>
          SUPPORT MODE ACTIVE — session expires {new Date(supportSession.until).toLocaleString()}
        </Alert>
      )}

      <PageHeader
        title={data.companyName}
        breadcrumbs={[
          { label: 'Platform', path: platformUrl('dashboard') },
          { label: 'Tenants', path: platformUrl('tenants') },
          { label: data.tenantCode },
        ]}
        secondaryActions={[
          { label: 'Edit Profile', onClick: () => toast.info('Edit (simulated)') },
          {
            label: 'Enter Support Mode',
            variant: 'warning',
            onClick: () => setSupportModal(true),
          },
        ]}
      />

      <Card className="mb-3">
        <Card.Body>
          <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
            <Badge bg="secondary">{data.tenantCode}</Badge>
            <StatusBadge status={data.status} />
            <Badge bg="primary">{data.plan}</Badge>
            <span className="text-muted small ms-2">
              Owner: <strong>{data.ownerName}</strong> · {data.ownerEmail}
            </span>
          </div>
          <div className="d-flex flex-wrap gap-2">
            <Dropdown>
              <Dropdown.Toggle variant="outline-primary" size="sm" id="plan-dd">
                Upgrade Plan
              </Dropdown.Toggle>
              <Dropdown.Menu>
                {['Starter', 'Growth', 'Enterprise'].map((p) => (
                  <Dropdown.Item key={p} onClick={() => toast.success(`Plan → ${p} (simulated)`)}>
                    {p}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
            <Button variant="outline-danger" size="sm" onClick={() => setSuspendModal(true)}>
              Suspend Tenant
            </Button>
          </div>
        </Card.Body>
      </Card>

      <Tab.Container activeKey={tab} onSelect={(k) => setTab(k || 'overview')}>
        <Nav variant="tabs" className="mb-3 flex-wrap">
          {['overview', 'subscription', 'integrations', 'usage', 'support', 'audit'].map((k) => (
            <Nav.Item key={k}>
              <Nav.Link eventKey={k} className="text-capitalize">
                {k === 'support' ? 'Support History' : k}
              </Nav.Link>
            </Nav.Item>
          ))}
        </Nav>
        <Tab.Content>
          <Tab.Pane eventKey="overview">
            <Row className="g-3 mb-4">
              {healthCards.map((h) => (
                <Col md={3} key={h.title}>
                  <Card>
                    <Card.Body>
                      <div className="text-muted small">{h.title}</div>
                      <div className="fs-4 fw-semibold">{h.value}</div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
            <h6>Onboarding</h6>
            <ul className="list-unstyled mb-4">
              {(data.onboardingItems || []).map((o) => (
                <li key={o.id} className="d-flex align-items-center gap-2 mb-2">
                  {onboardingIcon(o.status)}
                  {o.label}
                </li>
              ))}
            </ul>
            <Form.Group className="mb-3">
              <Form.Label>Internal notes</Form.Label>
              <Form.Control as="textarea" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
              <Button size="sm" className="mt-2" variant="primary" onClick={saveNote}>
                Save Note
              </Button>
            </Form.Group>
            <Card>
              <Card.Body>
                <Card.Title className="h6">Last support action</Card.Title>
                <p className="mb-0 small">{data.lastSupportAction?.summary}</p>
                <span className="text-muted small">
                  {data.lastSupportAction?.date && new Date(data.lastSupportAction.date).toLocaleString()}
                  {data.lastSupportAction?.admin && ` · ${data.lastSupportAction.admin}`}
                </span>
              </Card.Body>
            </Card>
          </Tab.Pane>

          <Tab.Pane eventKey="subscription" mountOnEnter>
            <Card className="mb-3">
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <p>
                      <strong>Plan:</strong> {sub?.planName}
                    </p>
                    <p>
                      <strong>Billing cycle:</strong> {sub?.billingCycle}
                    </p>
                    <p>
                      <strong>Amount:</strong> ${sub?.amountUsd}/mo
                    </p>
                    <p>
                      <strong>Discount:</strong> {sub?.discountPct}%
                    </p>
                  </Col>
                  <Col md={6}>
                    <p>
                      <strong>Contract term:</strong> {sub?.contractTerm}
                    </p>
                    <p>
                      <strong>Renewal:</strong> {sub?.renewalDate && new Date(sub.renewalDate).toLocaleDateString()}
                    </p>
                  </Col>
                </Row>
                <Button size="sm" className="me-2" onClick={() => toast.info('Change plan (simulated)')}>
                  Change Plan
                </Button>
                <Button size="sm" variant="outline-secondary" onClick={() => toast.info('Discount (simulated)')}>
                  Apply Discount
                </Button>
              </Card.Body>
            </Card>
          </Tab.Pane>

          <Tab.Pane eventKey="integrations" mountOnEnter>
            <Row className="g-3">
              {['microsoft', 'google', 'whatsapp'].map((key) => {
                const block = integ?.[key];
                const label = key[0].toUpperCase() + key.slice(1);
                return (
                  <Col md={4} key={key}>
                    <Card>
                      <Card.Body>
                        <Card.Title className="h6">{label}</Card.Title>
                        <p className="small mb-1">Token: {block?.tokenStatus}</p>
                        <p className="small mb-1">
                          Last sync: {block?.lastSync ? new Date(block.lastSync).toLocaleString() : '—'}
                        </p>
                        <p className="small mb-2">Webhook: {block?.webhookStatus}</p>
                        <Button size="sm" className="me-1" onClick={() => toast.success('Test OK (simulated)')}>
                          Run Test
                        </Button>
                        <Button size="sm" variant="outline-danger" onClick={() => toast.info('Token reset (simulated)')}>
                          Reset Token
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </Tab.Pane>

          <Tab.Pane eventKey="usage" mountOnEnter>
            {usage && (
              <div className="d-flex flex-column gap-3">
                {[
                  ['Users', usage.users.used, usage.users.limit],
                  ['Meetings', usage.meetings.used, usage.meetings.limit],
                  ['AI Tokens (M)', usage.aiTokensM.used, usage.aiTokensM.limit],
                  ['Storage GB', usage.storageGb.used, usage.storageGb.limit],
                ].map(([label, u, lim]) => {
                  const pct = Math.min(100, Math.round((u / lim) * 100));
                  return (
                    <div key={label}>
                      <div className="d-flex justify-content-between small mb-1">
                        <span>{label}</span>
                        <span>
                          {u} / {lim}
                        </span>
                      </div>
                      <ProgressBar now={pct} variant={usageColor(pct)} />
                    </div>
                  );
                })}
              </div>
            )}
          </Tab.Pane>

          <Tab.Pane eventKey="support" mountOnEnter>
            <ul className="list-unstyled">
              {(data.supportHistory || []).map((s) => (
                <li key={s.id} className="mb-3 pb-3 border-bottom">
                  <strong>{new Date(s.date).toLocaleString()}</strong> — {s.admin}
                  <div className="small text-muted">
                    {s.reason} · {s.durationMin}m · {s.outcome} · PII masked: {s.piiMasked ? 'yes' : 'no'}
                  </div>
                </li>
              ))}
            </ul>
          </Tab.Pane>

          <Tab.Pane eventKey="audit" mountOnEnter>
            <Table size="sm" responsive>
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Actor</th>
                  <th>Action</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {(data.tenantAudit || []).map((a) => (
                  <tr key={a.id}>
                    <td>{new Date(a.ts).toLocaleString()}</td>
                    <td>{a.actor}</td>
                    <td>{a.action}</td>
                    <td>{a.result}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>

      <ConfirmModal
        isOpen={supportModal}
        onClose={() => setSupportModal(false)}
        onConfirm={() => {
          setSupportModal(false);
          navigate(platformUrl(`tenants/${id}/support`));
        }}
        title="Enter support mode?"
        message="You will be redirected to the support session form."
        confirmLabel="Continue"
      />

      <ConfirmModal
        isOpen={suspendModal}
        onClose={() => setSuspendModal(false)}
        onConfirm={async () => {
          try {
            await suspendTenantAPI(id, 'Admin action');
            toast.success('Tenant suspended (simulated)');
            setSuspendModal(false);
            load();
          } catch (e) {
            toast.error(e.message);
          }
        }}
        title="Suspend tenant?"
        message="This will restrict access for all tenant users."
        confirmLabel="Suspend"
        danger
      />
    </div>
  );
}
